import { useState, useEffect, useMemo } from "react";
import { parseSemanticVersion } from "../../utilities/versionHelper";
import api from "../../utilities/api";

const CurrentVersionPanel = () => {
  const [records, setRecords] = useState([]);
  const [selectedVersion, setSelectedVersion] = useState("");
  const [savedVersion, setSavedVersion] = useState("");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  // Fetch records and the persisted current version on mount
  useEffect(() => {
    const fetchData = async () => {
      try {
        const [recordsRes, settingRes] = await Promise.allSettled([
          api.get(`/record`),
          api.get(`/settings/currentVersion`),
        ]);

        if (recordsRes.status === "fulfilled") {
          setRecords(recordsRes.value.data);
        }

        if (settingRes.status === "fulfilled") {
          const ver = settingRes.value.data.value;
          setSelectedVersion(ver);
          setSavedVersion(ver);
        }
      } catch (error) {
        console.error("Error fetching data:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  // Extract and sort unique versions descending (newest first)
  const availableVersions = useMemo(() => {
    const versionSet = new Set();
    records.forEach((record) => {
      const introduced = record.introduced;
      if (introduced) {
        const versionStr = Array.isArray(introduced)
          ? introduced.join(".")
          : String(introduced);
        if (versionStr) versionSet.add(versionStr);
      }
    });

    return Array.from(versionSet).sort((a, b) => {
      const parsedA = parseSemanticVersion(a) || [0, 0, 0];
      const parsedB = parseSemanticVersion(b) || [0, 0, 0];
      for (let i = 0; i < 3; i++) {
        const diff = parsedB[i] - parsedA[i];
        if (diff !== 0) return diff;
      }
      return 0;
    });
  }, [records]);

  // Persist the selected version to the backend
  const handleSelect = async (version) => {
    setSelectedVersion(version);
    setSaving(true);
    try {
      await api.put(`/settings/currentVersion`, {
        value: version,
      });
      setSavedVersion(version);
    } catch (error) {
      console.error("Error saving current version:", error);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="max-w-xl h-full flex flex-col">
      <h2 className="text-xl font-semibold text-gray-800 mb-2">
        Set Current Version
      </h2>
      <h3 className="text-sm text-gray-500 mb-6">
        Select a version to indicate the current version. This setting is
        persisted and will be applied for all users. To add a new version,
        create a record with the desired version in the &quot;introduced&quot;
        field.
      </h3>
      {savedVersion && (
        <p className="text-sm text-gray-500 mb-4">
          Current version:{" "}
          <span className="font-medium text-gray-700">{savedVersion}</span>
        </p>
      )}

      {loading ? (
        <p className="text-sm text-gray-500">Loading versions…</p>
      ) : availableVersions.length === 0 ? (
        <p className="text-sm text-gray-500">No versions found.</p>
      ) : (
        <div className="space-y-1 overflow-y-auto flex-1 min-h-0">
          {availableVersions.map((version) => (
            <label
              key={version}
              className="flex items-center gap-3 px-3 py-1 rounded-lg cursor-pointer hover:bg-gray-50 transition-colors"
            >
              <input
                type="radio"
                name="current-version"
                value={version}
                checked={selectedVersion === version}
                onChange={() => handleSelect(version)}
                disabled={saving}
                className="w-4 h-4 text-cyan-700 border-gray-300 focus:ring-cyan-500 focus:ring-2 accent-cyan-700"
              />
              <span className="text-sm text-gray-700">{version}</span>
            </label>
          ))}
        </div>
      )}
    </div>
  );
};

export default CurrentVersionPanel;
